import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Icon } from "../../ui";
import MetaAdsGraphAdPreview from "./MetaAdsGraphAdPreview";
import { metaAdsGradientCtaClass, metaAdsLinkClass } from "./metaAdsTheme";
import {
  META_ADS_PREVIEW_PLATFORM_FACEBOOK,
  META_ADS_PREVIEW_PLATFORM_INSTAGRAM,
  META_AD_PREVIEW_FORMAT_IDS,
  humanizeMetaAdPreviewFormatId,
} from "./metaAdsUtils";

function toInstagramStyleHandle(name) {
  if (!name || typeof name !== "string") {
    return "";
  }
  const s = name
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
  return s ? `@${s.slice(0, 30)}` : "";
}

function getHostnameForPreview(raw) {
  if (!raw || typeof raw !== "string") {
    return "";
  }
  const t = raw.trim();
  if (!t) {
    return "";
  }
  try {
    const withProto = /^https?:\/\//i.test(t) ? t : `https://${t}`;
    return new URL(withProto).hostname.replace(/^www\./i, "").toUpperCase();
  } catch {
    return "";
  }
}

function PreviewProfileAvatar({ logoSrc, fallbackLetter }) {
  const normalized =
    logoSrc != null && typeof logoSrc === "string" ? logoSrc.trim() : "";
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [normalized]);

  const letter =
    typeof fallbackLetter === "string" && fallbackLetter.trim()
      ? fallbackLetter.trim().charAt(0).toUpperCase()
      : "?";
  const showLogo = Boolean(normalized && !imgFailed);

  return (
    <div
      className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full",
        showLogo
          ? "bg-zinc-800"
          : "from-brand-cyan/90 to-brand-purple/90 bg-gradient-to-br text-xs font-bold text-white",
      )}
      aria-hidden
    >
      {showLogo ? (
        <img
          src={normalized}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="text-xs font-bold">{letter}</span>
      )}
    </div>
  );
}

function InstagramPreviewBlock({
  t,
  pageName,
  handle,
  accountLabel,
  primaryText,
  imageSrc,
  destinationLabel,
  businessLogo,
}) {
  const avatarLetter = (pageName?.trim() || accountLabel?.trim() || "?")
    .charAt(0)
    .toUpperCase();

  return (
    <>
      <div className="border-b border-white/10">
        <div className="flex items-center gap-2.5 px-3 py-2.5">
          <PreviewProfileAvatar
            logoSrc={businessLogo}
            fallbackLetter={avatarLetter}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <span className="truncate text-sm font-semibold tracking-tight">
                {handle}
              </span>
              <Icon
                name="BadgeCheck"
                className="h-3.5 w-3.5 shrink-0 text-[#4A9FF5]"
                strokeWidth={2.5}
                aria-hidden
              />
            </div>
            <p className="text-[11px] text-zinc-500">
              {t("create.previewSponsored")}
            </p>
          </div>
          <button
            type="button"
            className="shrink-0 rounded-md p-1 text-zinc-300 hover:text-white"
            tabIndex={-1}
            aria-label={t("create.previewOptionsAria")}
          >
            <Icon name="MoreHorizontal" className="h-5 w-5" aria-hidden />
          </button>
        </div>
      </div>

      <div className="relative aspect-square w-full bg-zinc-900">
        {imageSrc ? (
          <img src={imageSrc} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full min-h-[120px] items-center justify-center px-4 text-center text-xs text-zinc-500">
            {t("create.previewImagePlaceholder")}
          </div>
        )}
      </div>

      <div className="px-1.5 pt-1.5">
        <div className="flex items-center justify-between px-1 py-0.5">
          <div className="flex items-center gap-3.5">
            <button
              type="button"
              className="p-0.5 text-white hover:opacity-80"
              tabIndex={-1}
              aria-label={t("create.previewActionLikeAria")}
            >
              <Icon
                name="Heart"
                className="h-[22px] w-[22px]"
                strokeWidth={1.7}
              />
            </button>
            <button
              type="button"
              className="p-0.5 text-white hover:opacity-80"
              tabIndex={-1}
              aria-label={t("create.previewActionCommentAria")}
            >
              <Icon
                name="MessageCircle"
                className="h-[22px] w-[22px]"
                strokeWidth={1.7}
              />
            </button>
            <button
              type="button"
              className="p-0.5 text-white hover:opacity-80"
              tabIndex={-1}
              aria-label={t("create.previewActionShareAria")}
            >
              <Icon
                name="Send"
                className="h-[20px] w-[20px] -translate-y-0.5"
                strokeWidth={1.7}
              />
            </button>
          </div>
          <button
            type="button"
            className="p-0.5 text-white hover:opacity-80"
            tabIndex={-1}
            aria-label={t("create.previewActionSaveAria")}
          >
            <Icon
              name="Bookmark"
              className="h-[22px] w-[22px]"
              strokeWidth={1.7}
            />
          </button>
        </div>

        <p className="px-2 pt-0.5 pb-1 text-sm font-semibold">
          {t("create.previewLikes")}
          {accountLabel ? (
            <span className="font-normal text-zinc-400"> · {accountLabel}</span>
          ) : null}
        </p>

        <div className="px-2 pb-2 text-sm leading-relaxed text-zinc-200">
          <p className="line-clamp-4">
            <span className="me-1 font-semibold text-white">{handle}</span>
            {primaryText || t("create.previewTextPlaceholder")}
          </p>
        </div>

        <p className="px-2 pb-2 text-[11px] text-zinc-500">
          {t("create.previewTimeAgo")}
        </p>
      </div>

      <div className="border-t border-white/10 p-2.5">
        <button
          type="button"
          className={cn(
            "w-full rounded-md py-2 text-sm font-semibold",
            metaAdsGradientCtaClass,
          )}
          tabIndex={-1}
        >
          {t("create.previewCta")}
        </button>
      </div>

      <p
        className={cn(
          "px-3 pb-3 text-center text-xs break-all",
          metaAdsLinkClass,
        )}
      >
        {destinationLabel || "https://demo-campaign.com"}
      </p>
    </>
  );
}

function FacebookPreviewBlock({
  t,
  pageName,
  accountLabel,
  primaryText,
  imageSrc,
  destinationLabel,
  businessLogo,
}) {
  const displayName =
    pageName?.trim() ||
    accountLabel?.trim() ||
    t("create.previewFacebookPagePlaceholder");
  const domain = getHostnameForPreview(destinationLabel) || "—";
  const linkHeadline = displayName;
  const fbAvatarLetter = displayName?.trim()
    ? displayName.trim().charAt(0).toUpperCase()
    : "?";

  return (
    <>
      <div className="border-b border-white/10">
        <div className="flex items-center gap-2.5 px-2.5 py-2">
          <PreviewProfileAvatar
            logoSrc={businessLogo}
            fallbackLetter={fbAvatarLetter}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <span className="truncate text-sm font-semibold text-zinc-100">
                {displayName}
              </span>
              <Icon
                name="BadgeCheck"
                className="h-3.5 w-3.5 shrink-0 text-[#4A9FF5]"
                strokeWidth={2.5}
                aria-hidden
              />
            </div>
            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-zinc-500">
              <Icon name="Globe" className="h-3 w-3 shrink-0" aria-hidden />
              <span>{t("create.previewSponsored")}</span>
            </p>
          </div>
          <div className="flex shrink-0 items-center">
            <button
              type="button"
              className="rounded-md p-1 text-zinc-400 hover:text-white"
              tabIndex={-1}
              aria-label={t("create.previewOptionsAria")}
            >
              <Icon name="MoreHorizontal" className="h-5 w-5" aria-hidden />
            </button>
            <button
              type="button"
              className="rounded-md p-1 text-zinc-400 hover:text-white"
              tabIndex={-1}
              aria-label={t("create.previewFacebookCloseAria")}
            >
              <Icon name="X" className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      </div>

      <div className="px-3 pt-1 pb-2 text-sm leading-snug text-zinc-100">
        <p className="line-clamp-6 whitespace-pre-wrap">
          {primaryText || t("create.previewTextPlaceholder")}
        </p>
      </div>

      <div className="relative aspect-square w-full bg-zinc-900">
        {imageSrc ? (
          <img src={imageSrc} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full min-h-[120px] items-center justify-center px-4 text-center text-xs text-zinc-500">
            {t("create.previewImagePlaceholder")}
          </div>
        )}
      </div>

      <div className="bg-zinc-800/95 px-3 py-2.5">
        <div className="flex gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-medium tracking-wide text-zinc-500 uppercase">
              {domain}
            </p>
            <p className="line-clamp-2 text-sm font-semibold text-white">
              {linkHeadline}
            </p>
            <p className="line-clamp-2 text-xs text-zinc-400">
              {t("create.previewFacebookLinkDescription")}
            </p>
          </div>
          <div className="shrink-0 self-center">
            <span className="inline-flex rounded-md bg-zinc-600/90 px-2.5 py-1.5 text-xs font-semibold text-white">
              {t("create.previewCta")}
            </span>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 px-2 py-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2 text-[12px] text-zinc-400">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-0.5">
              <Icon name="ThumbsUp" className="h-4 w-4" strokeWidth={1.8} />
              <span className="text-zinc-300">
                {t("create.previewFacebookEngageLike")}
              </span>
            </span>
            <span className="text-zinc-600">·</span>
            <span className="inline-flex items-center gap-0.5">
              <Icon
                name="MessageCircle"
                className="h-4 w-4"
                strokeWidth={1.8}
              />
              <span>{t("create.previewFacebookEngageComment")}</span>
            </span>
            <span className="text-zinc-600">·</span>
            <span className="inline-flex items-center gap-0.5">
              <Icon name="Share2" className="h-4 w-4" strokeWidth={1.8} />
              <span>{t("create.previewFacebookEngageShare")}</span>
            </span>
          </div>
          <div
            className="flex h-5 w-5 items-center justify-center rounded-full bg-[#1877F2]"
            aria-hidden
          >
            <Icon
              name="ThumbsUp"
              className="h-2.5 w-2.5 text-white"
              strokeWidth={2.5}
            />
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-800/80 px-3 pt-1 pb-3">
        <p className={cn("mb-1.5 text-xs", metaAdsLinkClass)}>
          {t("create.previewFacebookViewMoreComments")}
        </p>
        <div className="flex gap-2">
          <div
            className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-zinc-700"
            aria-hidden
          />
          <div className="min-w-0 text-sm">
            <p>
              <span className="font-semibold text-zinc-200">
                {t("create.previewFacebookCommentName")}
              </span>{" "}
              <span className="text-zinc-300">
                {t("create.previewFacebookCommentBody")}
              </span>
            </p>
          </div>
        </div>
      </div>

      <p
        className={cn(
          "px-3 pb-3 text-center text-xs break-all",
          metaAdsLinkClass,
        )}
      >
        {destinationLabel || "https://demo-campaign.com"}
      </p>
    </>
  );
}

function MetaAdsGraphPreviewFeedItem({
  adId,
  graphLiveDraft,
  adFormat,
  title,
}) {
  const ref = useRef(null);
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldFetch(true);
            break;
          }
        }
      },
      { root: null, rootMargin: "200px 0px 400px 0px", threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <article ref={ref} className="mb-6 break-inside-avoid">
      <h3 className="text-foreground mb-2.5 line-clamp-2 text-[11px] font-semibold tracking-[0.08em] uppercase">
        {title}
      </h3>
      <MetaAdsGraphAdPreview
        adId={adId}
        graphLiveDraft={graphLiveDraft}
        adFormat={adFormat}
        fetchEnabled={shouldFetch}
        className="border-border w-full border border-white/10 bg-zinc-950/50 shadow-inner"
      />
    </article>
  );
}

const MetaAdsCreativePreview = ({
  metaAdPreviewAdId,
  primaryText,
  destinationLabel,
  imageSrc,
  pageName,
  accountLabel: accountLabelProp,
  businessLogo,
  className,
  graphLiveDraft,
  fullViewportColumn = false,
}) => {
  const { t } = useTranslation("metaAds");
  const graphAdId = String(metaAdPreviewAdId ?? "").trim();

  const baseId = useId();
  const [previewPlatform, setPreviewPlatform] = useState(
    META_ADS_PREVIEW_PLATFORM_INSTAGRAM,
  );

  const accountLabel = accountLabelProp?.trim() || "";

  const handle = useMemo(() => {
    const fromPage = toInstagramStyleHandle(pageName);
    if (fromPage) {
      return fromPage;
    }
    const fromAccount = toInstagramStyleHandle(accountLabel);
    if (fromAccount) {
      return fromAccount;
    }
    return t("create.previewUsernamePlaceholder");
  }, [pageName, accountLabel, t]);

  const tabIgId = `${baseId}-ig`;
  const tabFbId = `${baseId}-fb`;
  const panelId = `${baseId}-panel`;

  if (graphAdId) {
    return (
      <div
        className={cn(
          "border-border bg-card shadow-primary/5 flex min-h-0 flex-col overflow-hidden rounded-2xl border shadow-lg backdrop-blur-sm",
          fullViewportColumn
            ? "h-full min-h-0 flex-1"
            : "max-h-[88vh] lg:sticky lg:top-6 lg:max-h-[min(92dvh,960px)] lg:self-start",
          className,
        )}
      >
        <div className="from-brand-cyan/10 shrink-0 border-b border-white/10 bg-gradient-to-b to-transparent px-4 pt-4 pb-3.5 sm:px-5">
          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {t("create.previewTitle")}
          </p>
          <p className="text-muted-foreground mt-1.5 text-[11px] leading-relaxed">
            {t("create.previewFeedHint")}
          </p>
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4 [scrollbar-width:thin] sm:px-4"
          role="feed"
          aria-label={t("create.previewTitle")}
        >
          <div className="columns-1 [column-gap:1.25rem] [column-fill:balance]">
            {META_AD_PREVIEW_FORMAT_IDS.map((formatId) => (
              <MetaAdsGraphPreviewFeedItem
                key={formatId}
                adId={graphAdId}
                graphLiveDraft={graphLiveDraft}
                adFormat={formatId}
                title={humanizeMetaAdPreviewFormatId(formatId)}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  /*
   * Legacy mock preview (Instagram / Facebook tabs) for flows without a Meta ad id
   * (e.g. new campaign draft). When `metaAdPreviewAdId` is set, Graph previews load
   * in a masonry feed — see MetaAdsGraphAdPreview.
   */
  return (
    <div
      className={cn(
        "border-border bg-card shadow-primary/5 rounded-2xl border p-4 shadow-lg backdrop-blur-sm lg:sticky lg:top-6 lg:self-start",
        className,
      )}
    >
      <p className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
        {t("create.previewTitle")}
      </p>
      <div
        className="mx-auto max-w-sm overflow-hidden rounded-sm border border-white/10 bg-[#0d0d0d] text-white"
        dir="ltr"
      >
        <div
          className="flex border-b border-white/10 text-[11px]"
          role="tablist"
          aria-label={t("create.previewTitle")}
        >
          <button
            id={tabIgId}
            type="button"
            role="tab"
            aria-selected={
              previewPlatform === META_ADS_PREVIEW_PLATFORM_INSTAGRAM
            }
            aria-controls={panelId}
            className={cn(
              "flex-1 px-3 py-2.5 text-center font-semibold transition",
              previewPlatform === META_ADS_PREVIEW_PLATFORM_INSTAGRAM
                ? "border-b-2 border-white text-white"
                : "text-zinc-500 hover:text-zinc-300",
            )}
            onClick={() =>
              setPreviewPlatform(META_ADS_PREVIEW_PLATFORM_INSTAGRAM)
            }
            tabIndex={
              previewPlatform === META_ADS_PREVIEW_PLATFORM_INSTAGRAM ? 0 : -1
            }
            aria-label={t("create.previewTabInstagramAria")}
          >
            {t("create.previewPlatformInstagram")}
          </button>
          <button
            id={tabFbId}
            type="button"
            role="tab"
            aria-selected={
              previewPlatform === META_ADS_PREVIEW_PLATFORM_FACEBOOK
            }
            aria-controls={panelId}
            className={cn(
              "flex-1 px-3 py-2.5 text-center font-semibold transition",
              previewPlatform === META_ADS_PREVIEW_PLATFORM_FACEBOOK
                ? "border-b-2 border-white text-white"
                : "text-zinc-500 hover:text-zinc-300",
            )}
            onClick={() =>
              setPreviewPlatform(META_ADS_PREVIEW_PLATFORM_FACEBOOK)
            }
            tabIndex={
              previewPlatform === META_ADS_PREVIEW_PLATFORM_FACEBOOK ? 0 : -1
            }
            aria-label={t("create.previewTabFacebookAria")}
          >
            {t("create.previewPlatformFacebook")}
          </button>
        </div>

        <div
          id={panelId}
          role="tabpanel"
          aria-labelledby={
            previewPlatform === META_ADS_PREVIEW_PLATFORM_INSTAGRAM
              ? tabIgId
              : tabFbId
          }
        >
          {previewPlatform === META_ADS_PREVIEW_PLATFORM_INSTAGRAM ? (
            <InstagramPreviewBlock
              t={t}
              pageName={pageName}
              handle={handle}
              accountLabel={accountLabel}
              primaryText={primaryText}
              imageSrc={imageSrc}
              destinationLabel={destinationLabel}
              businessLogo={businessLogo}
            />
          ) : (
            <FacebookPreviewBlock
              t={t}
              pageName={pageName}
              accountLabel={accountLabel}
              primaryText={primaryText}
              imageSrc={imageSrc}
              destinationLabel={destinationLabel}
              businessLogo={businessLogo}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MetaAdsCreativePreview;
